<template>
  <div class="container-fluid p-0">
    <div class="vl-parent">
      <loading v-model:active="isLoading" :can-cancel="false" :is-full-page="fullPage" />
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body p-4">
        <form class="forms-sample" @submit.prevent="uploadFile">
          <!-- Volunteer Title -->
          <div class="form-group row align-items-center mb-4">
            <label class="col-sm-3 col-form-label font-weight-bold text-dark">
              Volunteer Title <span class="text-danger">*</span>
            </label>
            <div class="col-sm-9">
              <input
                type="text"
                class="form-control"
                v-model.trim="noticeTitle"
                placeholder="Enter volunteer title"
                required
              />
            </div>
          </div>

          <!-- File Upload -->
          <div class="form-group row align-items-start mb-4">
            <label class="col-sm-3 col-form-label font-weight-bold text-dark pt-2">
              File Upload <span class="text-danger">*</span>
            </label>
            <div class="col-sm-9">
              <!-- Hidden File Input -->
              <input
                type="file"
                ref="fileInput"
                @change="onFileChange"
                style="display: none;"
              />

              <!-- Browse Input Group -->
              <div class="input-group mb-2">
                <input
                  type="text"
                  readonly
                  class="form-control bg-light"
                  :value="fileNameDisplay"
                  placeholder="Select any file (Image, PDF, Word, Excel, ZIP, etc.)"
                  @click="triggerBrowse"
                  style="cursor: pointer;"
                />
                <div class="input-group-append">
                  <button class="btn btn-info px-4" type="button" @click="triggerBrowse">
                    <i class="fa fa-folder-open-o mr-1"></i> Browse File
                  </button>
                  <button
                    v-if="fileToUpload"
                    class="btn btn-outline-danger"
                    type="button"
                    @click="removeFile"
                    title="Remove File"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Selected File Info Details -->
              <div v-if="fileToUpload" class="mt-3 p-3 bg-light border rounded">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge badge-primary mr-2 text-uppercase font-weight-bold px-2 py-1">
                      {{ fileExtension || 'FILE' }}
                    </span>
                    <div>
                      <strong class="text-dark d-block text-truncate" style="max-width: 350px;">
                        {{ fileToUpload.name }}
                      </strong>
                      <small class="text-muted">{{ formattedFileSize }}</small>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                    @click="removeFile"
                  >
                    <i class="fa fa-trash"></i> Remove
                  </button>
                </div>

                <!-- Image Preview if image -->
                <div v-if="imagePreview" class="mt-3 pt-3 border-top">
                  <p class="text-muted font-weight-bold mb-2 small">Image Preview:</p>
                  <div class="position-relative d-inline-block border rounded bg-white p-1">
                    <img
                      :src="imagePreview"
                      alt="Preview"
                      class="img-fluid rounded"
                      style="max-height: 180px; max-width: 100%; object-fit: contain;"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Buttons -->
          <div class="form-group row mt-4">
            <div class="col-sm-9 offset-sm-3">
              <button
                type="submit"
                class="btn btn-success px-4 py-2 font-weight-bold"
                :disabled="isLoading"
              >
                <i class="fa fa-check-circle mr-1"></i> Submit Volunteer
              </button>
              <button
                type="button"
                class="btn btn-secondary px-3 py-2 ml-2"
                @click="resetForm"
                :disabled="isLoading"
              >
                Reset
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import axios from 'axios';

export default {
  name: 'CreateVolunteer',
  components: {
    Loading
  },
  data() {
    return {
      noticeTitle: '',
      fileToUpload: null,
      imagePreview: null,
      isLoading: false,
      fullPage: true,
    };
  },
  computed: {
    url() {
      return this.$store.state.url;
    },
    fileNameDisplay() {
      return this.fileToUpload ? this.fileToUpload.name : '';
    },
    fileExtension() {
      if (!this.fileToUpload) return '';
      const parts = this.fileToUpload.name.split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : '';
    },
    formattedFileSize() {
      if (!this.fileToUpload) return '';
      const bytes = this.fileToUpload.size;
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  },
  methods: {
    triggerBrowse() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.fileToUpload = file;

      // If selected file is an image, generate a live preview
      if (file.type && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },
    removeFile() {
      this.fileToUpload = null;
      this.imagePreview = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    resetForm() {
      this.noticeTitle = '';
      this.removeFile();
    },
    async uploadFile() {
      if (!this.noticeTitle) {
        toast.warning('Please enter a volunteer title', {
          position: 'top-center',
          autoClose: 2000
        });
        return;
      }

      if (!this.fileToUpload) {
        toast.warning('Please select a file to upload', {
          position: 'top-center',
          autoClose: 2000
        });
        return;
      }

      this.isLoading = true;
      const formData = new FormData();
      formData.append('DistrictNameTitle', this.noticeTitle);
      formData.append('file', this.fileToUpload);

      try {
        const apiUrl = (this.url || '') + 'createnewvolunteer';
        const response = await axios.post(apiUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        this.isLoading = false;
        this.resetForm();

        toast.success(response.data.message || 'Volunteer has been created successfully', {
          autoClose: 1500,
          transition: 'zoom',
          position: 'top-center'
        });
      } catch (error) {
        this.isLoading = false;
        let errMsg = 'Failed to upload volunteer document. Please try again.';
        if (error.response && error.response.data && error.response.data.message) {
          errMsg = error.response.data.message;
        } else if (error.response && error.response.data && error.response.data.errors) {
          const firstErr = Object.values(error.response.data.errors)[0];
          if (Array.isArray(firstErr) && firstErr.length > 0) {
            errMsg = firstErr[0];
          }
        }

        toast.error(errMsg, {
          autoClose: 3000,
          transition: 'zoom',
          position: 'top-center'
        });
      }
    }
  }
};
</script>

<style scoped>
.badge-primary {
  background-color: #007bff;
  color: #fff;
  border-radius: 4px;
}
</style>